import { useRef, useState } from "react";
import api from "@/lib/api";
import { Upload, File as FileIcon, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

// Reusable attachment box used by Projects & Assets.
// entityType: "projects" | "assets"
export default function AttachmentBox({ entityType, entityId, attachments = [], onChange }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  const uploadAndAttach = async (file) => {
    if (!file || !entityId) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data: att } = await api.post(`/${entityType}/${entityId}/attachments`, {
        file_id: up.id, name: file.name, note: null,
      });
      toast.success("Lampiran ditambahkan");
      onChange?.([...(attachments || []), att]);
    } catch (e) {
      toast.error("Gagal upload lampiran");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (fileId) => {
    if (!confirm("Hapus lampiran ini?")) return;
    try {
      await api.delete(`/${entityType}/${entityId}/attachments/${fileId}`);
      onChange?.((attachments || []).filter((a) => a.file_id !== fileId));
    } catch {
      toast.error("Gagal hapus lampiran");
    }
  };

  const formatSize = (n) => {
    if (!n) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-2" data-testid={`attachments-${entityType}`}>
      <div className="flex items-center justify-between">
        <div className="label-tag">Lampiran ({(attachments || []).length})</div>
        <label className="flex items-center gap-2 text-xs border border-forest-100 px-3 py-1.5 rounded-sm cursor-pointer bg-white hover:bg-forest-50">
          <Upload size={12} /> {busy ? "Mengupload…" : "Tambah File"}
          <input ref={inputRef} type="file" hidden data-testid={`attach-input-${entityType}`}
            onChange={(e) => e.target.files[0] && uploadAndAttach(e.target.files[0])} disabled={!entityId || busy} />
        </label>
      </div>
      {(!attachments || attachments.length === 0) && (
        <div className="text-xs text-forest-400 italic border border-dashed border-forest-100 p-3 rounded-sm text-center">
          Belum ada lampiran. Upload kontrak, foto, invoice pihak-ketiga, dsb.
        </div>
      )}
      {attachments?.map((a) => (
        <div key={a.file_id} data-testid={`attachment-${a.file_id}`}
             className="flex items-center gap-3 border border-forest-100 rounded-sm p-2 text-sm bg-white">
          <FileIcon size={16} className="text-forest-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{a.name}</div>
            <div className="text-[10px] text-forest-400">{a.content_type || "file"} · {formatSize(a.size)}</div>
          </div>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${a.file_id}?auth=${token}`} target="_blank" rel="noreferrer"
             className="p-1.5 hover:bg-forest-50 rounded-sm text-forest-400"><ExternalLink size={13} /></a>
          <button onClick={() => remove(a.file_id)} className="p-1.5 hover:bg-forest-50 rounded-sm text-[#C45A3D]"
            data-testid={`del-attachment-${a.file_id}`}><Trash2 size={13} /></button>
        </div>
      ))}
    </div>
  );
}
